import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './home.css';
import Social from './Social';
import ScrollDown from './ScrollDown';

type BootStep = {
  prefix: string;
  frames?: string[];
};

const bootSequence: BootStep[] = [
  { prefix: '> SYSTEM BOOT v1.3.7' },
  {
    prefix: '> Initializing Adewale interface',
    frames: ['.', '..', '...', '....', '..... OK'],
  },
  {
    prefix: '> Loading engineer profile',
    frames: ['.', '..', '...', '....', '..... OK'],
  },
  {
    prefix: '> Syncing experience modules',
    frames: ['.', '..', '...', '....', '..... OK'],
  },
  {
    prefix: '> Mounting project archive',
    frames: ['.', '..', '...', '....', '..... OK'],
  },
  {
    prefix: '> Opening contact channel',
    frames: ['.', '..', '...', '....', '..... OK'],
  },
  {
    prefix: '> Homepage ready',
    frames: ['.', '..', '...', '....', '..... OK'],
  },
];

const roles = [
  'Software Engineer',
  'ML Engineer',
  'Open Source Contributor',
  'Graduate Researcher',
];

const Home: React.FC = () => {
  // Skip the boot intro when deep-linking to a section so the scroll target is reachable
  // (the boot overlay locks body scroll via `boot-active`).
  const { pathname } = useLocation();
  const skipBoot = pathname !== '/';

  const [visibleLines, setVisibleLines] = useState<string[]>([]);
  const [bootComplete, setBootComplete] = useState(skipBoot);
  const [showBootSequence, setShowBootSequence] = useState(!skipBoot);
  const [roleIndex, setRoleIndex] = useState(0);
  const [roleFade, setRoleFade] = useState(true);

  useEffect(() => {
    if (skipBoot) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setVisibleLines(
        bootSequence.map((step) =>
          step.frames ? `${step.prefix}${step.frames[step.frames.length - 1]}` : step.prefix
        )
      );
      setBootComplete(true);
      setShowBootSequence(false);
      return;
    }

    let cancelled = false;
    const timers: number[] = [];

    const sleep = (delay: number) =>
      new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, delay);
        timers.push(timer);
      });

    const runBootSequence = async () => {
      const renderedLines: string[] = [];

      for (const step of bootSequence) {
        if (cancelled) {
          return;
        }

        if (!step.frames) {
          renderedLines.push(step.prefix);
          setVisibleLines([...renderedLines]);
          await sleep(340);
          continue;
        }

        renderedLines.push(`${step.prefix}${step.frames[0]}`);
        setVisibleLines([...renderedLines]);
        await sleep(180);

        for (let frameIndex = 1; frameIndex < step.frames.length; frameIndex += 1) {
          if (cancelled) {
            return;
          }

          renderedLines[renderedLines.length - 1] = `${step.prefix}${step.frames[frameIndex]}`;
          setVisibleLines([...renderedLines]);
          await sleep(frameIndex === step.frames.length - 1 ? 260 : 170);
        }
      }

      if (cancelled) {
        return;
      }

      setBootComplete(true);
      await sleep(520);

      if (!cancelled) {
        setShowBootSequence(false);
      }
    };

    runBootSequence();

    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [skipBoot]);

  // Word-rotate effect for subtitle
  useEffect(() => {
    const interval = setInterval(() => {
      setRoleFade(false);
      setTimeout(() => {
        setRoleIndex(i => (i + 1) % roles.length);
        setRoleFade(true);
      }, 350);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('boot-active', showBootSequence);

    return () => {
      document.body.classList.remove('boot-active');
    };
  }, [showBootSequence]);

  return (
    <section className="home section" id="home">
      {showBootSequence && (
        <div
          className={`home__boot ${bootComplete ? 'home__boot--complete' : ''}`}
          aria-label="Homepage boot sequence"
          role="presentation"
        >
          <div className="home__boot-panel home__boot-panel--left" />
          <div className="home__boot-panel home__boot-panel--right" />

          <div className="home__boot-terminal">
            {visibleLines.map((line, index) => {
              const isLastVisibleLine = index === visibleLines.length - 1;

              return (
                <div className="home__boot-line" key={line}>
                  {line}
                  {isLastVisibleLine && <span className="home__boot-cursor">█</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="home__container container grid">
        <div className="home__content grid">
          <div className="home__data">
            <span className="home__greeting">Hello, I'm</span>
            <h1 className="home__title">
              Adewale Adenle
            </h1>
            <h3 className={`home__subtitle ${roleFade ? 'home__subtitle--visible' : 'home__subtitle--hidden'}`}>
              {roles[roleIndex]}
            </h3>
            <p className="home__description">
              Master’s student in Computer Science at the Georgia Institute of Technology, specializing in machine learning and operating systems, with experience building scalable distributed systems and ML infrastructure.
            </p>
            
            <Social />
            
            <a href="/contact" className="button button--flex contact-button">
              Contact Me
              <svg
                className="button__icon"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M14.2199 21.9352C13.0399 21.9352 11.3699 21.1052 10.0499 17.1352L9.32988 14.9752L7.16988 14.2552C3.20988 12.9352 2.37988 11.2652 2.37988 10.0852C2.37988 8.91525 3.20988 7.23525 7.16988 5.90525L15.6599 3.07525C17.7799 2.36525 19.5499 2.57525 20.6399 3.65525C21.7299 4.73525 21.9399 6.51525 21.2299 8.63525L18.3999 17.1252C17.0699 21.1052 15.3999 21.9352 14.2199 21.9352ZM7.63988 7.33525C4.85988 8.26525 3.86988 9.36525 3.86988 10.0852C3.86988 10.8052 4.85988 11.9052 7.63988 12.8252L10.1599 13.6652C10.3799 13.7352 10.5599 13.9152 10.6299 14.1352L11.4699 16.6552C12.3899 19.4352 13.4999 20.4252 14.2199 20.4252C14.9399 20.4252 16.0399 19.4352 16.9699 16.6552L19.7999 8.16525C20.3099 6.62525 20.2199 5.36525 19.5699 4.71525C18.9199 4.06525 17.6599 3.98525 16.1299 4.49525L7.63988 7.33525Z"
                  fill="white"
                ></path>
                <path
                  d="M10.11 14.7052C9.92005 14.7052 9.73005 14.6352 9.58005 14.4852C9.29005 14.1952 9.29005 13.7152 9.58005 13.4252L13.16 9.83518C13.45 9.54518 13.93 9.54518 14.22 9.83518C14.51 10.1252 14.51 10.6052 14.22 10.8952L10.64 14.4852C10.5 14.6352 10.3 14.7052 10.11 14.7052Z"
                  fill="white"
                ></path>
              </svg>
            </a>
          </div>
          
          <div className="home__img-wrapper">
            <div className="home__img"></div>
          </div>
        </div>

        <ScrollDown />
      </div>
    </section>
  );
};

export default Home; 
